import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowRight,
  Wallet,
  CheckCircle2,
  Package,
  DollarSign,
  Truck,
} from 'lucide-react';
import { formatDate } from '@/lib/formatters';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SettlementShipment {
  id: string;
  shipment_id: string;
  tracking_number: string;
  shipment_value: number;
  delivery_fee: number;
  total_amount: number;
  created_at: string;
}

interface SettlementDetail {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  shipment_value: number;
  delivery_fee: number;
  total_amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  status: 'draft' | 'confirmed' | 'paid';
  created_at: string;
  created_by?: string | null;
  created_by_name?: string | null;
  confirmed_by?: string | null;
  confirmed_by_name?: string | null;
  confirmed_at?: string | null;
  items: SettlementShipment[];
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft':
      return 'bg-muted text-muted-foreground border-border';
    case 'confirmed':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'paid':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'pending':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft':
      return 'مسودة';
    case 'confirmed':
      return 'مؤكدة';
    case 'paid':
      return 'مدفوعة';
    case 'pending':
      return 'معلقة';
    default:
      return status;
  }
};

export default function SettlementDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, patch, loading } = useApi();

  const [settlement, setSettlement] = useState<SettlementDetail | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (id) fetchSettlement();
  }, [id]);

  const fetchSettlement = async () => {
    try {
      const response: any = await get(`/api/v1/dashboard/settlements/${id}`);
      const data = response?.data || response;
      setSettlement(data);
    } catch (error) {
      console.error('Failed to fetch settlement:', error);
    } finally {
      setHasFetched(true);
    }
  };

  const handleConfirm = async () => {
    if (!id) return;
    setConfirming(true);
    try {
      await patch(`/api/v1/dashboard/settlements/${id}/confirm`);
      toast.success('تم تأكيد التسوية بنجاح');
      fetchSettlement();
    } catch (error: any) {
      toast.warning(error?.message || 'تعذّر تأكيد التسوية');
    } finally {
      setConfirming(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/settlements');
  };

  if ((!hasFetched || loading) && !settlement) {
    return (
      <PageContainer>
        <div className="space-y-6">
          <LoadingSkeleton type="card" rows={1} />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <LoadingSkeleton type="card" rows={4} />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (hasFetched && !settlement) {
    return (
      <PageContainer>
        <EmptyState
          icon={<Wallet className="h-16 w-16" />}
          title="التسوية غير موجودة"
          description="لم يتم العثور على التسوية المطلوبة"
          action={{ label: 'العودة للقائمة', onClick: handleBack }}
        />
      </PageContainer>
    );
  }

  const items = settlement!.items ?? [];
  const totalShipmentValue = items.reduce((s, r) => s + Number(r.shipment_value), 0);
  const totalDeliveryFee   = items.reduce((s, r) => s + Number(r.delivery_fee), 0);
  const totalAmount        = items.reduce((s, r) => s + Number(r.total_amount), 0);

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6 space-y-4" dir="rtl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="h-9 w-9">
              <ArrowRight className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl md:text-2xl font-bold">تفاصيل التسوية</h1>
                  <Badge variant="outline" className={getStatusColor(settlement!.status)}>
                    {getStatusLabel(settlement!.status)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatDate(settlement!.period_start)} - {formatDate(settlement!.period_end)}
                </p>
              </div>
            </div>
          </div>

          {settlement!.status === 'draft' && (
            <Button onClick={handleConfirm} disabled={confirming} className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {confirming ? 'جاري التأكيد...' : 'تأكيد التسوية'}
            </Button>
          )}
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" dir="rtl">
        <SummaryCard
          label="قيمة الشحنات"
          value={`${Number(settlement!.shipment_value).toLocaleString()}`}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <SummaryCard
          label="رسوم التوصيل"
          value={`${Number(settlement!.delivery_fee).toLocaleString()}`}
          icon={<Truck className="h-5 w-5" />}
          highlight="blue"
        />
        <SummaryCard
          label="الإجمالي"
          value={`${Number(settlement!.total_amount).toLocaleString()}`}
          icon={<Wallet className="h-5 w-5" />}
          highlight="green"
        />
        <SummaryCard
          label="عدد الشحنات"
          value={String(settlement!.items?.length || 0)}
          icon={<Package className="h-5 w-5" />}
          highlight="orange"
        />
      </div>

      {/* Settlement Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" dir="rtl">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">معلومات التسوية</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between py-1.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground">العميل</span>
              <Link
                to={`/dashboard/tenants/${settlement!.tenant_id}`}
                className="text-sm font-medium text-primary hover:underline"
              >
                {settlement!.tenant_name || '-'}
              </Link>
            </div>
            <InfoRow label="تاريخ الإنشاء" value={formatDate(settlement!.created_at)} />
            {settlement!.created_by_name && (
              <InfoRow label="أنشأ بواسطة" value={settlement!.created_by_name} />
            )}
            {settlement!.confirmed_at && (
              <InfoRow label="تاريخ التأكيد" value={formatDate(settlement!.confirmed_at)} />
            )}
            {settlement!.confirmed_by_name && (
              <InfoRow label="اعتمد بواسطة" value={settlement!.confirmed_by_name} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">التفاصيل المالية</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow
              label="قيمة الشحنات"
              value={`${Number(settlement!.shipment_value).toLocaleString()}`}
            />
            <InfoRow
              label="رسوم التوصيل"
              value={`${Number(settlement!.delivery_fee).toLocaleString()}`}
              valueClass="text-blue-600 font-semibold"
            />
            <InfoRow
              label="الإجمالي"
              value={`${Number(settlement!.total_amount).toLocaleString()}`}
              valueClass="font-semibold"
            />
          </CardContent>
        </Card>
      </div>

      {/* Shipments Table */}
      <Card dir="rtl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            الشحنات المشمولة ({settlement!.items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<Package className="h-12 w-12" />}
                title="لا توجد شحنات"
                description="لا توجد شحنات مرتبطة بهذه التسوية"
              />
            </div>
          ) : (
            <div className="rounded-b-lg overflow-hidden">
              <Table dir="rtl">
                <TableHeader>
                  <TableRow className="h-10 bg-muted/50">
                    <TableHead className="text-end text-xs font-semibold px-4">رقم التتبع</TableHead>
                    <TableHead className="text-end text-xs font-semibold px-4">تاريخ الإنشاء</TableHead>
                    <TableHead className="text-end text-xs font-semibold px-4">الإجمالي</TableHead>
                    <TableHead className="text-end text-xs font-semibold px-4">رسوم التوصيل</TableHead>
                    <TableHead className="text-end text-xs font-semibold px-4">قيمة الشحنة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.id}
                      className="h-10 cursor-pointer hover:bg-muted/40"
                      onClick={() => navigate(`/dashboard/shipments/${item.shipment_id}`)}
                    >
                      <TableCell className="px-4 py-2 font-mono text-sm font-medium">{item.tracking_number}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-muted-foreground">{formatDate(item.created_at)}</TableCell>
                      <TableCell className="px-4 py-2 text-sm font-bold">{Number(item.total_amount).toLocaleString()}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-blue-600">{Number(item.delivery_fee).toLocaleString()}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-green-600 font-medium">{Number(item.shipment_value).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-muted/60 font-semibold border-t-2">
                    <TableCell className="px-4 py-3 text-sm font-bold" colSpan={2}>الإجمالي</TableCell>
                    <TableCell className="px-4 py-3 text-sm font-bold">{totalAmount.toLocaleString()}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-blue-600 font-bold">{totalDeliveryFee.toLocaleString()}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-green-600 font-bold">{totalShipmentValue.toLocaleString()}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

// Helper components

interface SummaryCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  highlight?: 'green' | 'blue' | 'orange';
}

function SummaryCard({ label, value, icon, highlight }: SummaryCardProps) {
  const highlightClasses = {
    green: 'border-green-500/50 bg-green-500/5',
    blue: 'border-blue-500/50 bg-blue-500/5',
    orange: 'border-orange-500/50 bg-orange-500/5',
  };

  const textClasses = {
    green: 'text-green-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600',
  };

  return (
    <Card className={cn('transition-colors', highlight && highlightClasses[highlight])}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={cn('text-muted-foreground', highlight && textClasses[highlight])}>
            {icon}
          </span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className={cn('text-lg font-bold', highlight && textClasses[highlight])}>{value}</p>
      </CardContent>
    </Card>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
  valueClass?: string;
}

function InfoRow({ label, value, valueClass }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={cn('text-sm font-medium', valueClass)}>{value}</span>
    </div>
  );
}
